import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAdminProducts } from "@/hooks/useAdminStore";
import { useAdminLinkMonitor } from "@/hooks/useAdminLinkMonitor";
import AdminHeader from "@/pages/admin/components/AdminHeader";
import ClicksChart from "@/pages/admin/reports/components/ClicksChart";
import MarketplaceChart from "@/pages/admin/reports/components/MarketplaceChart";
import AffiliateCoverage from "@/pages/admin/reports/components/AffiliateCoverage";
import TopProductsTable from "@/pages/admin/reports/components/TopProductsTable";
import ReportStatCard from "@/pages/admin/reports/components/ReportStatCard";
import BrokenLinksReport from "@/pages/admin/reports/components/BrokenLinksReport";

export type Period = "7d" | "30d" | "90d";
type ReportTab = "overview" | "broken-links";

export default function AdminReports() {
  const [period, setPeriod] = useState<Period>("30d");
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") === "broken-links" ? "broken-links" : "overview") as ReportTab;
  const { products } = useAdminProducts();
  const dailyDealProducts = useMemo(
    () => products.filter((product) => product.showInDailyDeals),
    [products]
  );
  const {
    report,
    records,
    brokenRecords,
    brokenCount,
    healthyCount,
    redirectedCount,
    isRunning,
    progress,
    runMonitor,
  } = useAdminLinkMonitor();

  const periodLabel: Record<Period, string> = {
    "7d": "Últimos 7 dias",
    "30d": "Últimos 30 dias",
    "90d": "Últimos 90 dias",
  };

  const totalClicks = useMemo(() => {
    const base = { "7d": 12480, "30d": 48920, "90d": 134750 };
    return base[period];
  }, [period]);

  const conversionRate = useMemo(() => {
    const base = { "7d": 3.2, "30d": 3.8, "90d": 4.1 };
    return base[period];
  }, [period]);

  const affiliateLinks = products.filter(
    (product) => product.affiliateLink && product.affiliateLink !== "#" && product.affiliateLink.startsWith("http")
  ).length;
  const dailyDealLinks = dailyDealProducts.filter(
    (product) => product.affiliateLink && product.affiliateLink !== "#" && product.affiliateLink.startsWith("http")
  ).length;

  const coveragePercent = products.length > 0
    ? Math.round((affiliateLinks / products.length) * 100)
    : 0;

  const handleTabChange = (tab: ReportTab) => {
    const nextParams = new URLSearchParams(searchParams);
    if (tab === "overview") {
      nextParams.delete("tab");
    } else {
      nextParams.set("tab", tab);
    }
    setSearchParams(nextParams);
  };

  return (
    <div>
      <AdminHeader
        title="Relatórios"
        subtitle="Análise de desempenho, cliques e monitoramento de links de afiliado"
      />

      <div className="flex items-center gap-2 mb-6">
        {([
          { id: "overview", label: "Visão Geral", icon: "ri-bar-chart-box-line" },
          { id: "broken-links", label: "Links Quebrados", icon: "ri-link-unlink-m" },
        ] as const).map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab.id
                ? "bg-orange-500 text-white"
                : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300"
            }`}
          >
            <i className={tab.icon}></i>
            {tab.label}
            {tab.id === "broken-links" && brokenCount > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs ${activeTab === tab.id ? "bg-white/20 text-white" : "bg-red-50 text-red-600"}`}>
                {brokenCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === "overview" ? (
        <>
          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm text-gray-500 font-medium mr-1">Período:</span>
            {(["7d", "30d", "90d"] as Period[]).map((value) => (
              <button
                key={value}
                onClick={() => setPeriod(value)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  period === value
                    ? "bg-orange-500 text-white"
                    : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300"
                }`}
              >
                {periodLabel[value]}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-4 gap-5 mb-7">
            <ReportStatCard
              icon="ri-cursor-line"
              label="Total de Cliques"
              value={totalClicks.toLocaleString("pt-BR")}
              trend={+12.4}
              color="orange"
              sub="em links de afiliado"
            />
            <ReportStatCard
              icon="ri-percent-line"
              label="Taxa de Conversão"
              value={`${conversionRate}%`}
              trend={+0.6}
              color="emerald"
              sub="cliques → compras"
            />
            <ReportStatCard
              icon="ri-links-line"
              label="Cobertura de Links"
              value={`${coveragePercent}%`}
              trend={coveragePercent >= 80 ? +5 : -8}
              color={coveragePercent >= 80 ? "emerald" : "red"}
              sub={`${affiliateLinks}/${products.length} produtos · ${dailyDealLinks}/${dailyDealProducts.length} ofertas`}
            />
            <ReportStatCard
              icon="ri-link-unlink-m"
              label="Links Quebrados"
              value={String(brokenCount)}
              trend={brokenCount === 0 ? 0 : -brokenCount}
              color={brokenCount === 0 ? "emerald" : "red"}
              sub={`${healthyCount} saudáveis · ${redirectedCount} redirecionados`}
            />
          </div>

          <div className="grid grid-cols-3 gap-6 mb-6">
            <div className="col-span-2 bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="font-bold text-gray-900">Cliques ao Longo do Tempo</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Evolução diária de cliques em links de afiliado</p>
                </div>
                <span className="text-xs font-semibold text-orange-500 bg-orange-50 px-3 py-1 rounded-full">
                  {periodLabel[period]}
                </span>
              </div>
              <ClicksChart period={period} />
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="mb-5">
                <h2 className="font-bold text-gray-900">Marketplaces</h2>
                <p className="text-xs text-gray-400 mt-0.5">Distribuição de cliques por plataforma</p>
              </div>
              <MarketplaceChart products={products} period={period} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2 bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="font-bold text-gray-900">Produtos Mais Clicados</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Ranking por volume de cliques no período</p>
                </div>
              </div>
              <TopProductsTable products={products} period={period} />
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="mb-5">
                <h2 className="font-bold text-gray-900">Cobertura de Afiliados</h2>
                <p className="text-xs text-gray-400 mt-0.5">Status dos links por categoria e nas Ofertas do Dia</p>
              </div>
              <AffiliateCoverage products={products} offers={dailyDealProducts} />
            </div>
          </div>
        </>
      ) : (
        <BrokenLinksReport
          records={brokenRecords}
          totalRecords={records.length}
          healthyCount={healthyCount}
          redirectedCount={redirectedCount}
          isRunning={isRunning}
          progress={progress}
          lastRunAt={report.lastRunAt}
          nextRunAt={report.nextRunAt}
          onRunNow={() => void runMonitor()}
        />
      )}
    </div>
  );
}
